import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, saveBlob } from '../../lib/api.js';
import type {
  PLCoverage,
  PLReport,
  ReconcileFlag,
  UndetectDay as UndetectDayRow,
  UndetectReconciliation,
  VolumeFlag,
} from '../../lib/types.js';
import { fmtInt, fmtMoney, todayIso } from '../../lib/format.js';
import { hiddenColumnsNote, visibleColumns } from '../../lib/visibleColumns.js';
import {
  VOLUME_LABEL,
  VOLUME_TITLE,
  defaultStatementMonth,
  ingestedBanner,
  latestStatementMonth,
  tieProblems,
  volumeTone,
} from '../../lib/pl-view.js';
import { errorMessage, fmtWhen, useIsAdmin } from '../settings/common.js';
import { tdClass, type Col } from '../table-bits.js';
import { useIsPhone } from '../../lib/use-media.js';

/**
 * The P&L card, its coverage header and the Airman-vs-bill reconciliation:
 * moved from the Stats page to the Costs tab in the 2026-09-14 redesign,
 * unchanged in what they compute and assert.
 */
export function PLCard({ pl }: { pl?: PLReport | undefined }) {
  // Hooks first: the early return below must not change their order.
  const isAdmin = useIsAdmin();
  const [month, setMonth] = useState('');
  // Phone (2026-09-14 mobile design): the four layers as cards with their
  // nets, the full lines, coverage and notes behind one button.
  const phone = useIsPhone();
  const [detailOpen, setDetailOpen] = useState(false);
  const showDetail = !phone || detailOpen;
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const download = useMutation({
    mutationFn: async (m: string) => {
      const { blob, filename } = await api.blob(
        `/api/pl/statement?month=${encodeURIComponent(m)}`,
        `statement_${m}.csv`,
      );
      saveBlob(blob, filename);
    },
    onMutate: () => setDownloadError(null),
    onError: (e) => setDownloadError(errorMessage(e)),
  });
  if (!pl) return null;
  const monthValue = month || defaultStatementMonth(pl.upto);
  // `neg` comes from the NUMBER, not the formatted string: fmtMoney renders a
  // loss as "$-1,234.56" (sign after the $), so startsWith('-') was never true
  // and a negative net silently lost its red.
  const row = (
    label: string,
    value: string,
    opts: { net?: boolean; note?: string | undefined; neg?: boolean; noteNeg?: boolean } = {},
  ) => (
    <div className={'pl-row' + (opts.net ? ' net' : '')} key={label}>
      <span>
        {label}
        {opts.note ? (
          <span className={'count' + (opts.noteNeg ? ' neg' : '')}> {opts.note}</span>
        ) : null}
      </span>
      <span className={opts.neg ? 'neg' : ''}>{value}</span>
    </div>
  );
  const cov = (v: number | null) => (v == null ? '' : `${v.toFixed(1)}% of all`);
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const o = pl.operating;
  const m = pl.matched;
  const g = pl.matchingGaps;
  const ov = pl.overheads;
  const und = pl.undetect;
  const ingestedReal = pl.ingestedCost?.semantics === 'real';
  const banner = pl.ingestedCost ? ingestedBanner(pl.ingestedCost) : null;
  // Revenue leaves out non-USD rows with no rate yet; Airman cost counts an
  // unreadable (NULL) cost as $0. Both are said next to the figure they
  // understate, in red, like fxMissing in the page header (R13, R28).
  const fxNote =
    o.fxMissing > 0 ? `${plural(o.fxMissing, 'row')} excluded — no USD rate yet` : undefined;
  const costUnknownNote =
    o.costUnknown > 0
      ? `${plural(o.costUnknown, 'row')} with unknown cost, counted as $0`
      : undefined;
  // The layers tie only when every check holds: operating = matched +
  // matching gaps + overheads, operating = matched + unattributed, AND
  // matched spend = Σ confirmed attribution (a real check - R10's outranking
  // provisional link shows up here and nowhere else). Say which one is off,
  // from the numbers, never from the server's word alone.
  const rec = pl.reconciliation;
  const problems = g && ov ? tieProblems(pl) : [];
  const ties = rec.ties && problems.length === 0;
  // The hosting bill: an overhead - in Operating, carried whole in
  // Overheads, never in Matched (nothing attributes it to a feed).
  // `h` is non-null once Civo is connected and synced; `h.days` is how many
  // of THIS window's days are mirrored. Zero means the sync has been failing
  // for longer than the window (or the rows are old): that is not a $0 bill,
  // so it renders as a red note, never as "Hosting −$0.00".
  const h = pl.hosting;
  const hostingMirrored = h != null && h.days > 0;
  const hostingNote = h
    ? `${h.days} day${h.days === 1 ? '' : 's'} · ${h.vatPct > 0 ? `incl. ${h.vatPct}% VAT` : 'ex-VAT'}` +
      (h.unpricedHours > 0 ? ` · ${fmtInt(h.unpricedHours)} h unpriced` : '')
    : '';
  const hostingMissing = (
    <div className="pl-row" key="Hosting (Civo)">
      <span>
        Hosting (Civo){' '}
        <span className="count neg">
          no mirrored hosting day in this window — sync Hosting under Configuration → Buy side
        </span>
      </span>
      <span className="neg">—</span>
    </div>
  );
  // What the long note says in red, for the phone summary where the note is folded.
  const phoneCaveats: string[] = [];
  if (und && und.coveredDays === 0)
    phoneCaveats.push(
      'No day of this window has a mirrored bill — the cost above is Airman’s allocation only; sync Undetect under Configuration → Buy side.',
    );
  if (h?.unpricedCodes.length)
    phoneCaveats.push(
      `Hosting is under-counted: no rate for ${h.unpricedCodes.join(', ')} (${fmtInt(h.unpricedHours)} hours) — enter one under Configuration → Buy side → Hosting.`,
    );
  if (pl.ingestedCost?.nonUsdRows)
    phoneCaveats.push(
      `${fmtInt(pl.ingestedCost.nonUsdRows)} non-USD row${pl.ingestedCost.nonUsdRows === 1 ? '' : 's'} carr${pl.ingestedCost.nonUsdRows === 1 ? 'ies' : 'y'} ingested cost charged at face value — no FX.`,
    );
  if (pl.duplicateSuspects.length)
    phoneCaveats.push(
      `Possible double count: ${pl.duplicateSuspects.map((d) => `feed ${d.feedId} (${d.provider}) under ${d.accounts.join(' and ')}, ${fmtMoney(d.revenue)} counted twice`).join('; ')}.`,
    );
  const otherItems = ov?.items ?? [];
  const otherNote = otherItems.length
    ? otherItems.map((i) => `${i.name} ${fmtMoney(i.charged)}`).join(' · ')
    : ov && ov.otherOverheads > 0
      ? 'pro-rata by day from Configuration → Buy side → Other costs'
      : undefined;
  return (
    <div className="table-card costcol">
      <div className="table-head">
        <h2>P&L — operating, matched, matching gaps, overheads</h2>
        <span className="count">
          {pl.since} → {pl.upto}
          {pl.partialDay ? ` · ${pl.partialDay} partial` : ''}
        </span>
        {isAdmin ? (
          <span className="pl-tools">
            <label className="count" htmlFor="statement-month">
              Statement
            </label>
            {/* max: the month of yesterday, the last complete day - a later
                month has no statement and the server refuses it (E7). */}
            <input
              id="statement-month"
              className="ui-input"
              type="month"
              value={monthValue}
              max={latestStatementMonth(todayIso()) || undefined}
              onChange={(e) => setMonth(e.target.value)}
              aria-label="Statement month"
            />
            <button
              type="button"
              className="ui-btn ui-btn--ghost ui-btn--sm"
              disabled={!/^\d{4}-\d{2}$/.test(monthValue) || download.isPending}
              onClick={() => download.mutate(monthValue)}
              title="The month's statement as CSV: revenue by rep and provider, Airman allocation, the launcher bill, hosting, other overheads, operating net, the invoice checks and the coverage figures."
            >
              {download.isPending ? 'Preparing…' : 'Download statement'}
            </button>
            {downloadError ? <span className="count neg">{downloadError}</span> : null}
          </span>
        ) : null}
      </div>
      {phone && g && ov ? (
        <div className="pl-summary">
          {[
            {
              name: 'Operating',
              net: o.net,
              hero: true,
              sub:
                `revenue ${fmtMoney(o.revenue)} − ` +
                (und && und.coveredDays === 0
                  ? `Airman's allocation ${fmtMoney(o.cost)} (no mirrored bill)`
                  : und
                    ? `bill ${fmtMoney(o.cost)}` +
                      (und.unmirroredAirmanCost
                        ? ` (incl. ${fmtMoney(und.unmirroredAirmanCost)} Airman on unmirrored days)`
                        : '')
                    : `Airman ${fmtMoney(o.airmanCost)}`) +
                (hostingMirrored ? ` − hosting ${fmtMoney(o.hostingCost)}` : '') +
                (ov.otherOverheads > 0 ? ` − other ${fmtMoney(ov.otherOverheads)}` : '') +
                (ingestedReal ? ` − ingested ${fmtMoney(o.ingestedCost)}` : '') +
                (!und && o.launchCost ? ` − launch ${fmtMoney(o.launchCost)}` : ''),
            },
            {
              name: 'Matched',
              net: m.net,
              hero: false,
              sub:
                `${plural(m.feeds, 'linked feed')} · ${fmtMoney(m.revenue)} revenue − ${fmtMoney(m.airmanCost)} attributed spend` +
                (!und && m.launchCost ? ` − launch ${fmtMoney(m.launchCost)}` : '') +
                (ingestedReal ? ` − ingested ${fmtMoney(m.ingestedCost)}` : ''),
            },
            {
              name: 'Matching gaps',
              net: g.net,
              hero: false,
              sub:
                `${plural(g.sellNoBuy, 'feed')} with revenue and no link · ${fmtMoney(g.revenue)} rev − ${fmtMoney(g.airmanCost)} unclaimed spend` +
                (!und && g.launchCost ? ` − launch ${fmtMoney(g.launchCost)}` : '') +
                (ingestedReal ? ` − ingested ${fmtMoney(g.ingestedCost)}` : ''),
            },
            {
              name: 'Overheads',
              net: ov.net,
              hero: false,
              sub:
                (und ? 'unattributed launches + proxy spend' : 'nothing attributed') +
                (hostingMirrored ? ' + hosting' : '') +
                (ov.otherOverheads > 0 ? ' + other' : '') +
                ' — no feed owns these',
            },
          ].map((l) => (
            <div className={'pl-sum' + (l.hero ? ' hero' : '')} key={l.name}>
              <div className="row">
                <h3>{l.name}</h3>
                <span className={'net' + (l.net < 0 ? ' neg' : l.hero ? ' gold' : '')}>
                  {fmtMoney(l.net)}
                </span>
              </div>
              <div className="sub">{l.sub}</div>
            </div>
          ))}
          <div className="table-note">
            Operating = Matched + Matching gaps + Overheads:{' '}
            {ties ? (
              <b>ties</b>
            ) : (
              <b className="neg">
                {problems.length ? problems.join('; ') : 'does not tie'} — report this
              </b>
            )}
            .{' '}
            {und && und.coveredDays > 0
              ? 'Cost basis is the Undetect bill.'
              : und
                ? 'Cost basis is Airman’s allocation until a bill day is mirrored.'
                : 'Cost basis is Airman’s allocation.'}
          </div>
          {/* The red caveats the detail carries are never folded away (P8). */}
          {phoneCaveats.length ? (
            <div className="table-note">
              <b className="neg">{phoneCaveats.join(' ')}</b>
            </div>
          ) : null}
          <button
            type="button"
            className="ui-btn ui-btn--ghost ui-btn--sm wide-btn"
            aria-expanded={detailOpen}
            onClick={() => setDetailOpen((v) => !v)}
          >
            {detailOpen ? 'Hide the detail' : 'Show the detail — every line, coverage, notes'}
          </button>
        </div>
      ) : null}
      {pl.coverage ? <CoverageHeader c={pl.coverage} /> : null}
      {banner ? (
        <div className="warnbox pl-banner" role="status">
          {banner}. Until then they count in the Cost tile above and nowhere in this P&L.
        </div>
      ) : null}
      {!g || !ov ? (
        <div className="warnbox">
          The server sent a three-layer P&L without Matching gaps and Overheads — the four layers
          cannot be shown until it is updated.
        </div>
      ) : !showDetail ? null : (
        <div className="pl-grid four">
          <div className="pl-layer hero">
            <h3>Operating</h3>
            <p className="kicker">
              {und
                ? 'Top-down: revenue against the launcher bill (Undetect). Independent of matching.'
                : 'Top-down from the sources. Independent of matching.'}
            </p>
            {row('Revenue, all feeds', fmtMoney(o.revenue), { note: fxNote, noteNeg: true })}
            {und
              ? row('Launcher bill (Undetect)', '−' + fmtMoney(o.cost - und.aiCost), {
                  note:
                    `${fmtInt(und.launches)} launches · ${und.gb.toLocaleString(undefined, { maximumFractionDigits: 1 })} GB` +
                    (und.unmirroredAirmanCost
                      ? ` · incl. ${fmtMoney(und.unmirroredAirmanCost)} Airman on unmirrored days`
                      : ''),
                })
              : row('Airman spend, all', '−' + fmtMoney(o.airmanCost), {
                  note: costUnknownNote,
                  noteNeg: true,
                })}
            {und && und.aiCost > 0
              ? row('Airman AI', '−' + fmtMoney(und.aiCost), {
                  note: 'conversion-action calls on the mirrored days · not in the bill, charged on top',
                })
              : null}
            {und
              ? row('Airman allocation, all', fmtMoney(o.airmanCost), {
                  note:
                    `${cov(und.costCoverage)} of the bill · breakdown, not added` +
                    (costUnknownNote ? ` · ${costUnknownNote}` : ''),
                  noteNeg: costUnknownNote != null,
                })
              : // The modelled launch line is legacy (LAUNCH_COST_PER_1K, normally 0): shown only when it is a number.
                o.launchCost
                ? row('Launch cost', '−' + fmtMoney(o.launchCost), {
                    note: `${fmtInt(o.committed)} committed`,
                  })
                : null}
            {h
              ? hostingMirrored
                ? row('Hosting (Civo)', '−' + fmtMoney(o.hostingCost), { note: hostingNote })
                : hostingMissing
              : null}
            {ov.otherOverheads > 0
              ? row('Other costs', '−' + fmtMoney(ov.otherOverheads), { note: otherNote })
              : null}
            {ingestedReal
              ? row('Ingested cost', '−' + fmtMoney(o.ingestedCost), {
                  note: 'per-row traffic, compute and LLM costs',
                })
              : null}
            {row('Operating net', fmtMoney(o.net), { net: true, neg: o.net < 0 })}
          </div>
          <div className="pl-layer">
            <h3>Matched</h3>
            <p className="kicker">
              {m.feeds} feed{m.feeds === 1 ? '' : 's'} with confirmed links
              {m.investigate ? ` · ${m.investigate} to investigate` : ''}
            </p>
            {row('Revenue, linked feeds', fmtMoney(m.revenue), { note: cov(m.revenueCoverage) })}
            {row('Attributed spend', '−' + fmtMoney(m.airmanCost), { note: cov(m.costCoverage) })}
            {und || !m.launchCost ? null : row('Launch cost', '−' + fmtMoney(m.launchCost))}
            {ingestedReal
              ? row('Ingested cost', '−' + fmtMoney(m.ingestedCost), { note: 'on linked feeds' })
              : null}
            {row('Matched net', fmtMoney(m.net), { net: true, neg: m.net < 0 })}
          </div>
          <div className="pl-layer">
            <h3>Matching gaps</h3>
            <p className="kicker">
              {g.sellNoBuy} feed{g.sellNoBuy === 1 ? '' : 's'} with revenue and no link
              {g.buyNoSell ? ` · ${g.buyNoSell} linked with spend but no stats yet (lag?)` : ''}
            </p>
            {row('Revenue no link claims', fmtMoney(g.revenue))}
            {row('Spend no link claims', '−' + fmtMoney(g.airmanCost))}
            {und || !g.launchCost ? null : row('Launch cost', '−' + fmtMoney(g.launchCost))}
            {ingestedReal
              ? row('Ingested cost', '−' + fmtMoney(g.ingestedCost), {
                  note: 'on feeds no link claims',
                })
              : null}
            {row('Matching gaps net', fmtMoney(g.net), { net: true, neg: g.net < 0 })}
          </div>
          <div className="pl-layer">
            <h3>Overheads</h3>
            <p className="kicker">Unattributed to any feed — never spread across feeds.</p>
            {/* The bill residual as exact, named costs (E23), worked back from
                Airman's per-destination rows: the launches Airman attributes
                to no destination at the launch rate, the proxy monthly fees,
                and the proxy spend its allocation does not explain. Negative
                spend means Airman claims MORE than the bill - shown with its
                true sign, and red, because its breakdown is wrong, not a
                saving. */}
            {und
              ? row(
                  'Launches not attached to a destination',
                  '−' + fmtMoney(ov.unattributedLaunch),
                  {
                    note: `${fmtInt(und.unattached.launches)} of ${fmtInt(und.launches)} launches (${und.unattached.share == null ? '—' : `${und.unattached.share.toFixed(1)}%`}) Airman attributes to no destination — pre-launch rolls, map generation, centifeed, or never registered — × the launch rate`,
                  },
                )
              : null}
            {und && ov.proxyBase > 0
              ? row('Proxy monthly fees', '−' + fmtMoney(ov.proxyBase), {
                  note: 'the fees on the Proxy rates schedule, per day pro-rata · inside the bill, never Airman’s to allocate',
                })
              : null}
            {und
              ? ov.unattributedSpend >= 0
                ? row(
                    'Proxy spend not attached to a destination',
                    '−' + fmtMoney(ov.unattributedSpend),
                    {
                      note: "the bill's bandwidth beyond Airman's per-visit allocation: those launches' bandwidth, plus the difference between Airman's flat price and the bill",
                    },
                  )
                : row('Airman over the bill', '+' + fmtMoney(-ov.unattributedSpend), {
                    note: 'Airman allocates more than was billed — check its rates',
                    neg: true,
                  })
              : null}
            {hostingMirrored
              ? row('Hosting (overhead)', '−' + fmtMoney(ov.hosting), { note: hostingNote })
              : null}
            {ov.otherOverheads > 0
              ? row('Other costs', '−' + fmtMoney(ov.otherOverheads), { note: otherNote })
              : null}
            {ingestedReal && ov.ingested > 0
              ? row('Ingested cost', '−' + fmtMoney(ov.ingested))
              : null}
            {row('Overheads net', fmtMoney(ov.net), { net: true, neg: ov.net < 0 })}
          </div>
        </div>
      )}
      {showDetail ? (
        <div className="table-note">
          Operating = Matched + Matching gaps + Overheads; Unattributed = Matching gaps + Overheads,
          so Operating = Matched + Unattributed:{' '}
          {ties ? (
            <b>ties</b>
          ) : (
            <b className="neg">
              {problems.length ? problems.join('; ') : 'does not tie'} — report this
            </b>
          )}
          . Spend no link claims is the sum of window gaps, unmatched destinations and unconfirmed
          suggestions; a matched feed over 90% margin is a red flag, not a win.
          {pl.partialDay ? (
            <>
              {' '}
              <b>{pl.partialDay} is today and still filling in</b> — its revenue is partial while
              its spend may be whole, so the nets read low until tomorrow.
            </>
          ) : null}
          {h ? (
            <>
              {' '}
              Hosting is the Civo bill mirrored at the list rates (
              {h.vatPct > 0 ? `incl. ${h.vatPct}% VAT` : 'ex-VAT'}) — an overhead in Operating,
              carried whole in Overheads (Unattributed), never spread across feeds.
              {/* `unpricedCodes` and `unpricedHours` are both THIS window's (read
                from the mirrored rows' line breakdown, not the last sync's
                31-day summary), so the codes named are the ones the hours
                belong to. */}
              {h.unpricedCodes.length ? (
                <>
                  {' '}
                  <b className="neg">
                    Hosting is under-counted: no rate for {h.unpricedCodes.join(', ')} (
                    {fmtInt(h.unpricedHours)} hours) — enter one under Configuration → Buy side →
                    Hosting.
                  </b>
                </>
              ) : null}
            </>
          ) : null}
          {ov && ov.otherOverheads > 0 ? (
            <>
              {' '}
              Other costs are the fixed monthly entries under Configuration → Buy side, charged per
              day pro-rata (monthly × days in window ÷ days in that month) — an overhead in
              Operating and Overheads, never in the tiles above.
            </>
          ) : null}
          {ingestedReal ? (
            <>
              {' '}
              Ingested cost is the per-row traffic, compute and LLM cost columns, confirmed as real
              under Configuration → Buy side; it is attributable per feed, so it appears in every
              layer — in Matched for the feeds with confirmed spend in this window, the rest in
              Overheads.
            </>
          ) : null}
          {pl.ingestedCost?.nonUsdRows ? (
            <>
              {' '}
              <b className="neg">
                {fmtInt(pl.ingestedCost.nonUsdRows)} non-USD row
                {pl.ingestedCost.nonUsdRows === 1 ? '' : 's'} carr
                {pl.ingestedCost.nonUsdRows === 1 ? 'ies' : 'y'} ingested cost charged at face value
                — no FX; confirm the columns' currency under Configuration → Buy side.
              </b>
            </>
          ) : null}
          {und ? (
            <>
              {' '}
              Cost basis is the <b>Undetect bill</b>; Airman's per-destination spend is its
              breakdown, so it is never added on top. What its attribution does not explain over the{' '}
              {und.coveredDays} mirrored days is worked back from its own per-destination rows and
              held as exact costs, never spread across feeds: the launches attached to no
              destination (× the launch rate), the proxy monthly fees, and the proxy spend beyond
              Airman's allocation. Part of every day is usage no destination owns — pre-launch
              rolls, map generation, centifeed — so a day is flagged only when its Airman share is
              unusual for this window, not for being short of the bill.
              {und.coveredDays === 0 ? (
                <>
                  {' '}
                  <b className="neg">No day of this window has a mirrored bill</b> — the cost above
                  is Airman's allocation only; sync Undetect under Configuration → Buy side.
                </>
              ) : null}
            </>
          ) : null}
          {pl.duplicateSuspects.length ? (
            <>
              {' '}
              <b className="neg">
                Possible double count:{' '}
                {pl.duplicateSuspects
                  .map(
                    (d) =>
                      `feed ${d.feedId} (${d.provider}) under ${d.accounts.join(' and ')}, ${fmtMoney(d.revenue)} counted twice` +
                      (d.currency && d.currency !== 'USD' ? ` (${d.currency} rows, in USD)` : ''),
                  )
                  .join('; ')}
                .
              </b>{' '}
              Identical rows under two accounts are flagged here, never removed — decide which pull
              is the copy.
            </>
          ) : null}
        </div>
      ) : null}
      {und ? <ReconciliationTable und={und} /> : null}
    </div>
  );
}

/**
 * How much of the window each figure covers, per source. A fraction short of
 * the window is gold; a source with no day at all is red - the number below
 * it is then Airman's allocation, or nothing, not a bill. A bill that is not
 * charged (no key, no completed sync) reads "not charged", never a count:
 * rows a Disconnect left behind are not a bill covering the window (E10).
 * "Days with revenue rows" is what the count IS - a successful crawl that
 * returned no rows looks the same as no crawl, so it never says "crawled".
 */
export function CoverageHeader({ c }: { c: PLCoverage }) {
  const days = (n: number | null, of: number) =>
    n == null
      ? { text: 'not charged', cls: '' }
      : {
          text: `${fmtInt(n)} / ${fmtInt(of)} days`,
          cls: n <= 0 && of > 0 ? 'neg' : n < of ? 'short' : '',
        };
  const when = (iso: string | null) => (iso ? fmtWhen(iso) : 'never');
  const facts: { label: string; text: string; cls?: string; title?: string }[] = [
    {
      label: 'Days with revenue rows',
      ...days(c.revenueDays, c.windowDays),
      title:
        'days in the window with at least one stat row - a crawl that returned no rows looks the same as no crawl',
    },
    {
      label: 'Airman',
      ...days(c.airmanDays, c.windowDays),
      title: 'days with an Airman buy-side row',
    },
    {
      label: 'Bill mirrored',
      ...days(c.billDays, c.windowDays),
      title:
        'days the launcher bill (Undetect) is mirrored; "not charged" until Undetect is connected and synced',
    },
    {
      label: 'Hosting mirrored',
      ...days(c.hostingDays, c.windowDays),
      title:
        'days the hosting bill (Civo) is mirrored; "not charged" until Civo is connected and synced',
    },
    {
      label: 'Last sync',
      text: `Airman ${when(c.lastSync.airman)} · Undetect ${when(c.lastSync.undetect)} · Civo ${when(c.lastSync.civo)}`,
    },
  ];
  if (c.partialDay)
    facts.push({
      label: 'Partial day',
      text: c.partialDay,
      cls: 'short',
      title: 'today: still filling in',
    });
  if (c.fxMissing > 0)
    facts.push({
      label: 'Excluded rows',
      text: `${fmtInt(c.fxMissing)} — no USD rate yet`,
      cls: 'neg',
      title: 'non-USD rows excluded from revenue, never counted at face value',
    });
  if (c.costUnknown > 0)
    facts.push({
      label: 'Unknown cost',
      text: `${fmtInt(c.costUnknown)} Airman rows as $0`,
      cls: 'neg',
      title: 'buy-side rows whose Airman cost was unreadable',
    });
  if (c.unpricedCodes.length)
    facts.push({
      label: 'Unpriced hosting',
      text: c.unpricedCodes.join(', '),
      cls: 'neg',
      title: 'Civo codes with hours in the window and no rate',
    });
  return (
    <dl className="pl-coverage" aria-label="Coverage of the window">
      {facts.map((f) => (
        <div key={f.label} title={f.title}>
          <dt>{f.label}</dt>
          <dd className={f.cls}>{f.text}</dd>
        </div>
      ))}
    </dl>
  );
}

const FLAG_LABEL: Record<ReconcileFlag, string> = {
  ok: 'ok',
  lag: 'lag',
  gap: 'gap',
  over: 'over',
  unmirrored: 'no bill yet',
};
const FLAG_TITLE: Record<ReconcileFlag, string> = {
  ok: "Airman's share of the day's bill is within tolerance of the window's usual share",
  lag: 'the bill has spend and Airman has not reported this far yet — lag until proven otherwise',
  gap: "Airman attributes less of this day's bill than it usually does over this window (tolerance points below the usual share), or skipped the day while reporting later ones",
  over: 'Airman claims MORE than the bill — its rates or registrations are wrong',
  unmirrored:
    'Airman has rows but the bill has not been mirrored for this day (older than the 30-day series)',
};

/**
 * Airman's breakdown against the bill, day by day. Two verdicts per day, both
 * flags rather than judgement calls: the cost verdict (within tolerance,
 * lagging, a real gap, Airman over-claiming) and the volume verdict beside it
 * (launches vs Airman's visits) - a launch gap beyond tolerance flags even
 * when the money agrees (P8). Sorted newest first so today's lag sits at the
 * top where it is expected, and the gaps further down are the ones to chase.
 */
export function ReconciliationTable({ und }: { und: UndetectReconciliation }) {
  const [open, setOpen] = useState(false);
  const phone = useIsPhone();
  const rows = [...und.daily].reverse();
  const f = und.flags;
  const vf = und.volumeFlags;
  const tone = (flag: ReconcileFlag) =>
    flag === 'ok' ? 'ok' : flag === 'lag' || flag === 'unmirrored' ? 'warn' : 'error';
  // Day and both verdicts never hide; a bill column reads as empty on an
  // unmirrored day (it renders "—"), so a window with no mirrored day hides
  // the bill side and says so rather than showing a column of dashes.
  const mirrored = (d: UndetectDayRow, v: number) => (d.flag === 'unmirrored' ? null : v);
  const cols: Col<UndetectDayRow>[] = [
    {
      key: 'Day',
      head: 'l',
      fixed: true,
      get: (d) => d.day,
      cellClass: 'l mono',
      cell: (d) => d.day,
    },
    {
      key: 'Launches',
      get: (d) => mirrored(d, d.undetectLaunches),
      cell: (d) => (d.flag === 'unmirrored' ? '—' : fmtInt(d.undetectLaunches)),
    },
    { key: 'Airman visits', get: (d) => d.airmanVisits, cell: (d) => fmtInt(d.airmanVisits) },
    {
      key: 'Launch gap',
      get: (d) => mirrored(d, d.launchGap),
      cellClass: (d) => (d.launchGap > 0 ? 'neg' : ''),
      cell: (d) => (d.flag === 'unmirrored' ? '—' : fmtInt(d.launchGap)),
    },
    {
      key: 'GB',
      get: (d) => mirrored(d, d.undetectGb),
      cell: (d) => (d.flag === 'unmirrored' ? '—' : d.undetectGb.toFixed(2)),
    },
    {
      key: 'Bill',
      get: (d) => mirrored(d, d.undetectCost),
      cell: (d) => (d.flag === 'unmirrored' ? '—' : fmtMoney(d.undetectCost)),
    },
    { key: 'Airman cost', get: (d) => d.airmanCost, cell: (d) => fmtMoney(d.airmanCost) },
    { key: 'Airman AI', get: (d) => d.airmanAiCost, cell: (d) => fmtMoney(d.airmanAiCost) },
    {
      key: 'Cost gap',
      get: (d) => mirrored(d, d.costGap),
      cellClass: (d) => (d.flag === 'gap' || d.flag === 'over' ? 'neg' : ''),
      cell: (d) =>
        d.flag === 'unmirrored'
          ? '—'
          : `${fmtMoney(d.costGap)}${d.costGapPct == null ? '' : ` (${d.costGapPct.toFixed(1)}%)`}`,
    },
    {
      key: 'Not attached (launches)',
      get: (d) => mirrored(d, d.unattributedLaunch),
      cell: (d) => (d.flag === 'unmirrored' ? '—' : fmtMoney(d.unattributedLaunch)),
    },
    {
      key: 'Not attached (spend)',
      get: (d) => mirrored(d, d.unattributedSpend),
      cellClass: (d) => (d.unattributedSpend < 0 ? 'neg' : ''),
      cell: (d) => (d.flag === 'unmirrored' ? '—' : fmtMoney(d.unattributedSpend)),
    },
    {
      key: 'Verdict',
      head: 'l',
      fixed: true,
      get: (d) => d.flag,
      cellClass: 'l',
      cell: (d) => (
        <span className={`ui-pill ui-pill--${tone(d.flag)}`} title={FLAG_TITLE[d.flag]}>
          {FLAG_LABEL[d.flag]}
        </span>
      ),
    },
    {
      key: 'Volume',
      head: 'l',
      fixed: true,
      get: (d) => d.volumeFlag,
      cellClass: 'l',
      // No bill for the day means nothing to compare launches to: a dash,
      // never "no launches", which would read as a fact about the launcher.
      cell: (d) =>
        d.flag === 'unmirrored' || !d.volumeFlag ? (
          <span className="dim" title="no bill mirrored for this day">
            —
          </span>
        ) : (
          <span
            className={`ui-pill ui-pill--${volumeTone(d.volumeFlag)}`}
            title={VOLUME_TITLE[d.volumeFlag]}
          >
            {VOLUME_LABEL[d.volumeFlag]}
          </span>
        ),
    },
  ];
  const { shown, hidden } = visibleColumns(rows, cols);
  return (
    <div className="recon">
      <div className="table-head">
        <h3>Airman vs the bill</h3>
        <span className="count">
          {und.from ?? '—'} → {und.to ?? '—'} · Airman covers{' '}
          {und.costCoverage == null ? '—' : `${und.costCoverage.toFixed(1)}%`} of cost,{' '}
          {und.launchCoverage == null ? '—' : `${und.launchCoverage.toFixed(1)}%`} of launches · gap{' '}
          {fmtMoney(und.costGap)} · bill{' '}
          {und.billPerLaunch == null ? '—' : `$${und.billPerLaunch.toFixed(4)}`}/launch vs Airman{' '}
          {und.airmanPerVisit == null ? '—' : `$${und.airmanPerVisit.toFixed(4)}`}/visit
        </span>
        {phone ? null : (
          <button
            type="button"
            className="ui-btn ui-btn--ghost ui-btn--sm"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Hide days' : 'Show days'}
          </button>
        )}
      </div>
      <p className="table-note recon-unattached">
        Not attached to any destination: <b>{fmtInt(und.unattached.launches)}</b> of{' '}
        {fmtInt(und.launches)} launches
        {und.unattached.share == null ? '' : ` (${und.unattached.share.toFixed(1)}%)`} ·{' '}
        <b>{fmtMoney(und.unattached.usd)}</b> of the bill
        {und.baseUsd > 0 ? ` (incl. ${fmtMoney(und.baseUsd)} of monthly fees)` : ''} — pre-launch
        rolls, map generation, centifeed, and anything Airman never registered, worked back from
        Airman's per-destination rows. Usual Airman share this window:{' '}
        {und.typicalCostCoverage == null
          ? 'not enough judged days'
          : `${und.typicalCostCoverage.toFixed(1)}% of cost, ${und.typicalLaunchCoverage == null ? '—' : `${und.typicalLaunchCoverage.toFixed(1)}%`} of launches`}
        {und.typicalCostCoverage == null
          ? ' — no day is called unusual with fewer than three to judge'
          : ' — a day well below it is a gap'}
        .
      </p>
      <div className="recon-flags">
        <span className="count">cost</span>
        {(['ok', 'lag', 'gap', 'over', 'unmirrored'] as ReconcileFlag[]).map((k) =>
          f[k] ? (
            <span key={k} className={`ui-pill ui-pill--${tone(k)}`} title={FLAG_TITLE[k]}>
              {f[k]} {FLAG_LABEL[k]}
            </span>
          ) : null,
        )}
        {vf ? (
          <>
            <span className="count">volume</span>
            {(['ok', 'lag', 'gap', 'over', 'none'] as VolumeFlag[]).map((k) =>
              vf[k] ? (
                <span
                  key={`v-${k}`}
                  className={`ui-pill ui-pill--${volumeTone(k)}`}
                  title={VOLUME_TITLE[k]}
                >
                  {vf[k]} {VOLUME_LABEL[k]}
                </span>
              ) : null,
            )}
          </>
        ) : null}
        <span className="count">
          unusual = {und.tolerancePct} points from the window's usual share · over ={' '}
          {und.tolerancePct}% above the bill
        </span>
      </div>
      {phone ? (
        // Phone: day, bill, cost gap and the cost verdict for the newest
        // days; the full table (launches, GB, the volume verdict) is a
        // desktop view - the flags above still count both verdicts.
        <div className="recon-rows">
          {(open ? rows : rows.slice(0, 5)).map((d) => (
            <div className="rr" key={d.day}>
              <span className="mono">{d.day.slice(5)}</span>
              <span>{d.flag === 'unmirrored' ? '—' : fmtMoney(d.undetectCost)}</span>
              <span className={d.flag === 'gap' || d.flag === 'over' ? 'neg' : ''}>
                {d.flag === 'unmirrored' || d.costGapPct == null
                  ? '—'
                  : `${d.costGapPct.toFixed(1)}%`}
              </span>
              <span className={`ui-pill ui-pill--${tone(d.flag)}`} title={FLAG_TITLE[d.flag]}>
                {FLAG_LABEL[d.flag]}
              </span>
            </div>
          ))}
          {rows.length > 5 ? (
            <button
              type="button"
              className="ui-btn ui-btn--ghost ui-btn--sm wide-btn"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? 'Show the newest 5 days' : `Show all ${rows.length} days`}
            </button>
          ) : null}
        </div>
      ) : open ? (
        <>
          <div className="table-scroll">
            <table className="stat-table">
              <thead>
                <tr>
                  {shown.map((c) => (
                    <th key={c.key} className={c.head}>
                      {c.key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.day}>
                    {shown.map((c) => (
                      <td key={c.key} className={tdClass(c, d)}>
                        {c.cell(d)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hidden.length ? <div className="table-note">{hiddenColumnsNote(hidden)}</div> : null}
        </>
      ) : null}
    </div>
  );
}
